import { useEffect, useRef, useState } from "react";
import { Cloud, CloudOff, RefreshCcw, Trash2, X, AlertTriangle } from "lucide-react";
import {
  useOfflineQueue,
  drainQueue,
  getQueueEntries,
  clearQueue,
  discardEntry,
} from "../lib/offlineQueue";
import { isNonQueueable } from "../lib/api";

/**
 * Sync indicator + queue manager.
 *
 * States:
 *   • Offline            → red "Offline" pill (non-clickable).
 *   • Online + 0 queued  → hidden (default).
 *   • Online + N queued  → amber "Syncing · N" pill. Click opens a panel
 *                          listing every pending mutation with Retry & Discard
 *                          actions, plus a "Clear all" shortcut so stale items
 *                          trapped in IndexedDB (e.g. from an old 5xx loop)
 *                          can be nuked by the user.
 *
 * Used at the top-right of both desktop and mobile headers.
 */
export default function OfflineIndicator({ compact = false }) {
  const { online, queueSize } = useOfflineQueue();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const refresh = async () => {
    const list = await getQueueEntries();
    setEntries(list);
  };

  useEffect(() => {
    if (open) refresh();
  }, [open, queueSize]);

  // Click-outside to dismiss
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (online && queueSize === 0) return null;

  const base =
    "inline-flex items-center gap-1.5 rounded-full text-[10px] uppercase tracking-wider font-bold transition";
  const padding = compact ? "px-2 py-1" : "px-2.5 py-1";

  if (!online) {
    return (
      <div
        className={`${base} ${padding} border border-rose-500/50 bg-rose-500/15 text-rose-200`}
        data-testid="offline-indicator-offline"
        title="No network — your changes are being saved on this device and will sync when you're back online."
      >
        <CloudOff className="w-3 h-3" />
        Offline
        {queueSize > 0 && <span className="ml-1 font-mono">· {queueSize}</span>}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${base} ${padding} border border-amber-500/50 bg-amber-500/15 text-amber-200 hover:bg-amber-500/20`}
        data-testid="offline-indicator-pending"
        title="Pending changes waiting to sync — click to review."
      >
        <RefreshCcw className={`w-3 h-3 ${busy ? "hv-spin-slow" : ""}`} />
        Syncing · {queueSize}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 w-[min(22rem,calc(100vw-2rem))] max-h-[70vh] z-50 bg-[#121419] border border-white/10 rounded-lg shadow-2xl overflow-hidden"
          data-testid="sync-queue-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <div className="text-xs uppercase tracking-wider text-amber-300 font-bold flex items-center gap-2">
              <RefreshCcw className="w-3 h-3" /> Pending sync · {queueSize}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-[#647087] hover:text-white"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-3 pt-2 pb-1 text-[11px] text-[#a0aabf] leading-relaxed">
            Requests saved while offline (or after a temporary server error).
            They replay automatically when the network returns. If they're
            stuck, retry or discard them here.
          </div>

          <div className="max-h-[45vh] overflow-auto divide-y divide-white/5">
            {entries.length === 0 ? (
              <div className="px-3 py-4 text-xs text-[#647087] italic">Queue is empty.</div>
            ) : (
              entries.map((e) => {
                const stale = isNonQueueable(e.url || "");
                return (
                <div
                  key={e.id}
                  className={`px-3 py-2 flex items-start gap-2 hover:bg-white/5 ${stale ? "bg-rose-500/[0.04]" : ""}`}
                  data-testid={`sync-queue-entry-${e.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono truncate text-white">
                      <span className="text-amber-300">{e.method}</span>{" "}
                      {shortUrl(e.url)}
                    </div>
                    <div className="text-[10px] text-[#647087] mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{new Date(e.created_at).toLocaleString('en-GB')}</span>
                      {e.attempts ? (
                        <span className="text-rose-300">
                          {e.attempts} failed attempt{e.attempts === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>
                    {e.last_error && (
                      <div className="text-[10px] text-rose-300/80 mt-0.5 flex items-start gap-1 leading-snug" title={e.last_error}>
                        <AlertTriangle className="w-2.5 h-2.5 mt-0.5 shrink-0" />
                        <span className="line-clamp-2 break-words">{e.last_error}</span>
                      </div>
                    )}
                    {stale && (
                      <div className="text-[10px] text-amber-300/80 mt-0.5">
                        AI / one-shot endpoint — replaying may duplicate work. Recommend discard.
                      </div>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      await discardEntry(e.id);
                      await refresh();
                    }}
                    className="text-rose-300 hover:text-rose-200 p-1"
                    title="Discard this pending change"
                    data-testid={`sync-queue-discard-${e.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                );
              })
            )}
          </div>

          <div className="flex items-center gap-2 p-2 border-t border-white/10">
            <button
              onClick={async () => {
                setBusy(true);
                await drainQueue();
                await refresh();
                setBusy(false);
              }}
              disabled={busy || entries.length === 0}
              className="hv-btn hv-btn-primary flex-1 text-xs"
              data-testid="sync-queue-retry-all"
            >
              <RefreshCcw className={`w-3 h-3 ${busy ? "hv-spin-slow" : ""}`} />
              {busy ? "Retrying…" : "Retry all"}
            </button>
            {entries.some((e) => isNonQueueable(e.url || "")) && (
              <button
                onClick={async () => {
                  const stale = entries.filter((e) => isNonQueueable(e.url || ""));
                  if (!stale.length) return;
                  if (!window.confirm(`Drop ${stale.length} AI / one-shot request${stale.length === 1 ? "" : "s"}? These can't be safely retried.`)) return;
                  for (const e of stale) {
                    // eslint-disable-next-line no-await-in-loop
                    await discardEntry(e.id);
                  }
                  await refresh();
                }}
                className="hv-btn hv-btn-ghost text-xs !text-amber-300"
                title="Drop AI / non-idempotent entries (TTS, transcribe, uploads)"
                data-testid="sync-queue-drop-ai"
              >
                <AlertTriangle className="w-3 h-3" /> Drop AI
              </button>
            )}
            <button
              onClick={async () => {
                if (!window.confirm(`Discard all ${queueSize} pending change(s)? This cannot be undone.`)) return;
                await clearQueue();
                await refresh();
              }}
              disabled={entries.length === 0}
              className="hv-btn hv-btn-ghost text-xs !text-rose-300"
              data-testid="sync-queue-clear-all"
            >
              <Trash2 className="w-3 h-3" /> Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function shortUrl(url) {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

export function OnlinePill() {
  // Tiny "all good" indicator for desktop only — green dot.
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300"
      title="Connected"
      data-testid="online-indicator"
    >
      <Cloud className="w-3 h-3" />
    </span>
  );
}
